import {
  HeatingOrCoolingSource,
  PerformanceTableFragment,
} from '../../../generated/graphql'
import { getPerformanceFromTable } from '../util/performance-table-utility'
import { calculateUsedCapacity, CoolingHeatPumpResult } from './heat-pump-util'

export type CoolingCalculationParams = {
  performanceTable: PerformanceTableFragment
  copModifier: number
  heatExhaustAvailable: boolean
  heatingPower: number
  freeCoolingSource?: HeatingOrCoolingSource
  freeGroundCoolingMaxPower?: number
  freeAirCoolingMaxTemperature?: number
  freeCoolingEer?: number
  coolingSource?: HeatingOrCoolingSource
  groundExhaustTemperature?: number
  coolingTemperature?: number
  coolingDemand?: number
  powerCapacity: number
  outdoorTemperature: number
}

export type CoolingCalculationResults = {
  freeCooling: CoolingHeatPumpResult
  active: CoolingHeatPumpResult
  powerCapacity: number
}

function getSourceTemperature(
  source: HeatingOrCoolingSource | undefined,
  groundExhaustTemperature: number | undefined,
  outdoorTemperature: number,
): number | null {
  switch (source) {
    case HeatingOrCoolingSource.Ground:
      return groundExhaustTemperature ?? null

    case HeatingOrCoolingSource.Air:
      return outdoorTemperature

    case HeatingOrCoolingSource.EnergyBus:
      // Business decision during hybrid heat pump development (verified later in April 2023 from Joni Hilpinen): This temperature should come from the energy bus, using a hardcoded value until that is implemented.
      return 7

    default:
      return null
  }
}

function calculateProvidedFreeCooling(
  freeCoolingSource: HeatingOrCoolingSource,
  freeGroundCoolingMaxPower: number | undefined,
  freeAirCoolingMaxTemperature: number | undefined,
  freeCoolingEer: number | undefined,
  coolingTemperature: number | undefined,
  coolingDemand: number,
  outdoorTemperature: number,
) {
  switch (freeCoolingSource) {
    case HeatingOrCoolingSource.Ground:
      if (freeCoolingEer === undefined) {
        return 0
      }

      return Math.min(coolingDemand, freeGroundCoolingMaxPower ?? Infinity)

    case HeatingOrCoolingSource.Air:
      if (freeAirCoolingMaxTemperature === undefined) {
        return 0
      }

      return outdoorTemperature < freeAirCoolingMaxTemperature
        ? coolingDemand
        : 0

    default:
      return 0
  }
}

function provideFreeCooling(
  freeCoolingSource: HeatingOrCoolingSource | undefined,
  freeGroundCoolingMaxPower: number | undefined,
  freeAirCoolingMaxTemperature: number | undefined,
  freeCoolingEer: number | undefined,
  coolingTemperature: number | undefined,
  coolingDemand: number,
  outdoorTemperature: number,
) {
  if (
    freeCoolingSource === undefined ||
    freeCoolingSource === HeatingOrCoolingSource.None ||
    freeCoolingEer === undefined ||
    coolingDemand === 0
  ) {
    return {
      coolingDemand,
      providedCooling: 0,
      heatToSource: 0,
    }
  }

  const providedCooling = calculateProvidedFreeCooling(
    freeCoolingSource,
    freeGroundCoolingMaxPower,
    freeAirCoolingMaxTemperature,
    freeCoolingEer,
    coolingTemperature,
    coolingDemand,
    outdoorTemperature,
  )

  return {
    coolingDemand: coolingDemand - providedCooling,
    providedCooling,
    heatToSource: providedCooling * (1 + 1 / freeCoolingEer),
  }
}

function provideActiveCooling(
  performanceTable: PerformanceTableFragment,
  copModifier: number,
  heatExhaustAvailable: boolean,
  heatingPower: number,
  coolingSource: HeatingOrCoolingSource | undefined,
  groundExhaustTemperature: number | undefined,
  coolingTemperature: number | undefined,
  coolingDemand: number,
  powerCapacity: number,
  outdoorTemperature: number,
) {
  const coolingSourceTemperature = getSourceTemperature(
    coolingSource,
    groundExhaustTemperature,
    outdoorTemperature,
  )
  if (
    !heatExhaustAvailable ||
    coolingSource === undefined ||
    coolingTemperature === undefined ||
    coolingDemand === 0 ||
    coolingSourceTemperature === null
  ) {
    return {
      coolingDemand,
      providedCooling: 0,
      heatToSource: 0,
      eer: NaN,
      powerCapacity,
    }
  }

  const performance = getPerformanceFromTable(
    performanceTable,
    coolingTemperature,
    coolingSourceTemperature,
    copModifier,
  )
  const eer = performance.cop - 1

  const coolingPower =
    (heatingPower * powerCapacity * performance.powerCoefficient * eer) /
    performance.cop

  const providedCooling = Math.min(coolingDemand, coolingPower)

  const usedCapacity = calculateUsedCapacity(
    providedCooling,
    heatingPower,
    performance.powerCoefficient,
  )

  return {
    coolingDemand: coolingDemand - providedCooling,
    providedCooling,
    heatToSource: providedCooling * (1 + 1 / eer),
    eer,
    powerCapacity: powerCapacity - usedCapacity,
  }
}

export function calculateCoolingForHour({
  performanceTable,
  copModifier,
  heatExhaustAvailable,
  heatingPower,
  freeCoolingSource,
  freeGroundCoolingMaxPower,
  freeAirCoolingMaxTemperature,
  freeCoolingEer,
  coolingSource,
  groundExhaustTemperature,
  coolingDemand,
  coolingTemperature,
  powerCapacity,
  outdoorTemperature,
}: CoolingCalculationParams): CoolingCalculationResults {
  if (!heatExhaustAvailable || !coolingDemand) {
    return {
      active: {
        providedCooling: 0,
        heatToSource: { type: HeatingOrCoolingSource.None, amount: 0 },
        eer: NaN,
      },
      freeCooling: {
        providedCooling: 0,
        heatToSource: { type: HeatingOrCoolingSource.None, amount: 0 },
        eer: NaN,
      },
      powerCapacity,
    }
  }

  const {
    coolingDemand: coolingDemandAfterFreeCooling,
    providedCooling: providedFreeCooling,
    heatToSource: heatToSourceFromFreeCooling,
  } = provideFreeCooling(
    freeCoolingSource,
    freeGroundCoolingMaxPower,
    freeAirCoolingMaxTemperature,
    freeCoolingEer,
    coolingTemperature,
    coolingDemand ?? 0,
    outdoorTemperature,
  )

  const {
    providedCooling: providedActiveCooling,
    heatToSource: heatToSourceFromActiveCooling,
    eer: activeCoolingEer,
    powerCapacity: powerCapacityAfterActiveCooling,
  } = provideActiveCooling(
    performanceTable,
    copModifier,
    heatExhaustAvailable,
    heatingPower,
    coolingSource,
    groundExhaustTemperature,
    coolingTemperature,
    coolingDemandAfterFreeCooling,
    powerCapacity,
    outdoorTemperature,
  )

  return {
    freeCooling: {
      providedCooling: providedFreeCooling,
      heatToSource: {
        type: freeCoolingSource ?? HeatingOrCoolingSource.None,
        amount: heatToSourceFromFreeCooling,
      },
      eer: freeCoolingEer ?? NaN,
    },
    active: {
      providedCooling: providedActiveCooling,
      heatToSource: {
        type: coolingSource ?? HeatingOrCoolingSource.None,
        amount: heatToSourceFromActiveCooling,
      },
      eer: activeCoolingEer,
    },
    powerCapacity: powerCapacityAfterActiveCooling,
  }
}
